import { useQuery } from '@apollo/client';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { GCTextLogo } from 'shared-components/images';
import { useWindowSize } from 'shared-components/utils/CustomHooks';
import { styled } from '@mui/system';
import { Button } from '@mui/material';
import DefaultHeader from './DefaultHeader';
import OutageBanner from 'op-components/Banner/Outage';
import { LOGGED_IN_PATIENT_QUERY } from './HeaderBarQueries';

interface Props {
  children: any;
  primaryRole: string;
  headingText?: string | null;
  menuButton?: boolean;
  menuButtonText?: string;
}

const HeaderContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  padding: 0px 15px 0px 15px;
  background-color: white;
  height: 56px;
  position: sticky;
  z-index: 50;
  box-shadow: 0px 3px 8px ${(props) => props.theme.palette.grey[600]};
  align-items: center;
`;

const StyledLogo = styled(GCTextLogo)`
  height: 28px;
`;

const StyledHeadingText = styled('div')`
  color: black;
  font-size: 23px;
  font-weight: bold;
`;

const PatientFacingHeader = ({
  children,
  primaryRole,
  headingText,
  menuButton,
  menuButtonText,
}: Props): JSX.Element => {
  const windowSize = useWindowSize();
  const isSmallDisplay = windowSize.width < 1440;

  const { loading, data, error } = useQuery(LOGGED_IN_PATIENT_QUERY, { fetchPolicy: 'network-only' });

  if (loading) {
    return <DefaultHeader patientFacing={true} />;
  }

  const patientId = data?.patient?.id;
  if (!patientId) {
    return <DefaultHeader patientFacing={true} />;
  }

  const MenuButton = (): JSX.Element => {
    return (
      <Link to={`/patient/${patientId}/home`}>
        <Button data-testid="go-to-home">{menuButtonText || 'Main Menu'}</Button>
      </Link>
    );
  };

  if (data) {
    return (
      <Fragment>
        <OutageBanner />
        <HeaderContainer id="patient-facing-header">
          <Link to={`/patient/${patientId}/home`} id="gc-logo-patient-facing">
            <StyledLogo />
          </Link>
          {headingText && isSmallDisplay && <StyledHeadingText>{headingText}</StyledHeadingText>}
          {menuButton && <MenuButton />}
        </HeaderContainer>
        {children && React.cloneElement(children, { primaryRole })}
      </Fragment>
    );
  }
  return <DefaultHeader patientFacing={true} />;
};

export default PatientFacingHeader;
